import { Timestamp, addDoc, collection, deleteDoc, doc, getDocs, onSnapshot, query, updateDoc, where } from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { useAuth, useIsAdmin } from '../auth/hooks';
import { HybridIcon } from '../components/HybridIcon';
import { useToast } from '../components/ToastProvider';
import { DEFAULT_ORGANIZATION_SETTINGS, mergeOrganizationSettings } from '../constants/organizationSettings';
import { getDB } from '../firebase/config';
import { usePWAInstall } from '../hooks/usePWAInstall';
import type { Invitation, OrganizationSettings, User } from '../models';
import { settingsApi } from '../services/api';
import { useTheme } from '../theme/ThemeProvider';
import { logOrganizationUpdate, logUpdate, logUserManagement } from '../utils/audit';
import './Settings.css';

export default function Settings() {
  const {
    user,
    organization,
    firebaseUser,
    isLocalImpersonation,
    refreshLocalImpersonationData,
  } = useAuth();
  const isAdmin = useIsAdmin();
  
  const [activeTab, setActiveTab] = useState<'profile' | 'organization' | 'staff' | 'app'>('profile');
  const { isInstallable, isInstalled, install } = usePWAInstall();
  const { theme, setTheme, effectiveTheme } = useTheme();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Profile form state
  const [displayName, setDisplayName] = useState(user?.displayName || '');
  const [email, setEmail] = useState(user?.email || '');

  // Organization form state
  const [organizationName, setOrganizationName] = useState(organization?.name || '');
  const [orgSettings, setOrgSettings] = useState<OrganizationSettings>(mergeOrganizationSettings());
  const [domainInput, setDomainInput] = useState('');

  // Staff management state
  const [staffMembers, setStaffMembers] = useState<User[]>([]);
  const [pendingInvitations, setPendingInvitations] = useState<Invitation[]>([]);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'admin' | 'member'>('member');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteCustomerEmail, setInviteCustomerEmail] = useState('');
  const [inviteCustomerLoading, setInviteCustomerLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Load staff members and invitations
  useEffect(() => {
    if (!user?.organizationId || !isAdmin) {
      setStaffMembers([]);
      setPendingInvitations([]);
      return;
    }

    if (isLocalImpersonation) {
      void loadStaffMembers();
      void loadPendingInvitations();
      return;
    }

    loadStaffMembers();
    loadPendingInvitations();

    const db = getDB();

    const unsubscribeStaff = onSnapshot(
      query(collection(db, 'users'), where('organizationId', '==', user.organizationId)),
      (snapshot) => {
        const members = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        } as User));
        setStaffMembers(members);
      }
    );

    const unsubscribeInvitations = onSnapshot(
      query(collection(db, 'invitations'), where('organizationId', '==', user.organizationId)),
      (snapshot) => {
        const invitations = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        } as Invitation));
        setPendingInvitations(invitations.filter(inv => !inv.usedAt));
      }
    );

    return () => {
      unsubscribeStaff();
      unsubscribeInvitations();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, isLocalImpersonation, user?.organizationId]);

  // Update form values when user/org data changes
  useEffect(() => {
    if (user) {
      setDisplayName(user.displayName);
      setEmail(user.email);
    }
    if (organization) {
      setOrganizationName(organization.name);
      setOrgSettings(mergeOrganizationSettings(organization.settings));
      setDomainInput('');
    } else {
      setOrgSettings(mergeOrganizationSettings());
    }
  }, [user, organization]);

  useEffect(() => {
    if (!isLocalImpersonation || !user?.organizationId) {
      return;
    }

    let isCancelled = false;

    const loadLocalOrganization = async () => {
      try {
        const currentOrganization = await settingsApi.getCurrentOrganization();
        if (isCancelled) {
          return;
        }

        setOrganizationName(currentOrganization.name);
        setOrgSettings(mergeOrganizationSettings(currentOrganization.settings));
      } catch (err) {
        if (!isCancelled) {
          console.error('Error loading local organization settings:', err);
        }
      }
    };

    void loadLocalOrganization();

    return () => {
      isCancelled = true;
    };
  }, [isLocalImpersonation, user?.organizationId]);

  const normalizeDomain = (domain: string) => domain.trim().toLowerCase().replace(/^@/, '');

  const isValidDomain = (domain: string) => /^[a-z0-9.-]+\.[a-z]{2,}$/i.test(domain);

  const sanitizeDomains = (domains: string[]) => {
    const normalized = domains
      .map(normalizeDomain)
      .filter((domain) => domain && isValidDomain(domain));
    return Array.from(new Set(normalized));
  };

  const formatDateValue = (value: unknown): string | null => {
    if (!value) {
      return null;
    }

    if (typeof value === 'object' && value !== null && 'toDate' in value && typeof (value as { toDate: () => Date }).toDate === 'function') {
      return (value as { toDate: () => Date }).toDate().toLocaleDateString();
    }

    const parsedDate = value instanceof Date ? value : new Date(String(value));
    if (Number.isNaN(parsedDate.getTime())) {
      return null;
    }

    return parsedDate.toLocaleDateString();
  };

  const toIsoTimestamp = (value?: Timestamp) => {
    if (!value) {
      return undefined;
    }

    return value.toDate().toISOString();
  };

  const buildInvitePayload = (email: string, role: Invitation['role'], settingsOverride?: OrganizationSettings) => ({
    email,
    role,
    expiresAt: toIsoTimestamp(getInviteExpiryTimestamp(settingsOverride)),
  });

  const buildSettingsForSave = (settings: OrganizationSettings = orgSettings): OrganizationSettings => {
    const inviteExpiryDays = Math.min(
      90,
      Math.max(1, Math.round(settings.inviteExpiryDays || DEFAULT_ORGANIZATION_SETTINGS.inviteExpiryDays))
    );
    const bookingWindowStartDays = Math.max(0, Math.round(settings.booking.bookingWindowStartDays));
    const bookingWindowEndDays = Math.max(bookingWindowStartDays, Math.round(settings.booking.bookingWindowEndDays));
    const cancellationCutoffHours = Math.max(0, Math.round(settings.booking.cancellationCutoffHours));

    return {
      ...settings,
      allowedDomains: sanitizeDomains(settings.allowedDomains),
      inviteExpiryDays,
      booking: {
        ...settings.booking,
        bookingWindowStartDays,
        bookingWindowEndDays,
        cancellationCutoffHours,
      },
      notifications: {
        ...settings.notifications,
      },
    };
  };

  const getInviteExpiryTimestamp = (settingsOverride?: OrganizationSettings) => {
    const settingsToUse = buildSettingsForSave(settingsOverride ?? orgSettings);
    const expireMs = settingsToUse.inviteExpiryDays * 24 * 60 * 60 * 1000;
    return Timestamp.fromDate(new Date(Date.now() + expireMs));
  };

  const handleAddDomain = () => {
    const normalized = normalizeDomain(domainInput);
    if (!normalized) {
      setError('Enter a domain to add');
      return;
    }
    if (!isValidDomain(normalized)) {
      setError('Enter a valid domain (e.g., example.com)');
      return;
    }
    if (orgSettings.allowedDomains.includes(normalized)) {
      setError('Domain already added');
      return;
    }

    setOrgSettings((prev) => ({
      ...prev,
      allowedDomains: [...prev.allowedDomains, normalized],
    }));
    setDomainInput('');
    setError('');
  };

  const handleRemoveDomain = (domain: string) => {
    setOrgSettings((prev) => ({
      ...prev,
      allowedDomains: prev.allowedDomains.filter((d) => d !== domain),
    }));
  };

  const loadStaffMembers = async () => {
    if (!user?.organizationId) return;

    try {
      if (isLocalImpersonation) {
        const members = await settingsApi.getStaff();
        setStaffMembers(members);
        return;
      }

      const db = getDB();
      const usersQuery = query(
        collection(db, 'users'),
        where('organizationId', '==', user.organizationId)
      );
      const snapshot = await getDocs(usersQuery);
      const members = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as User));
      setStaffMembers(members);
    } catch (err) {
      console.error('Error loading staff members:', err);
    }
  };

  const loadPendingInvitations = async () => {
    if (!user?.organizationId) return;

    try {
      if (isLocalImpersonation) {
        const invitations = await settingsApi.getInvitations();
        setPendingInvitations(invitations.filter(inv => !inv.usedAt));
        return;
      }

      const db = getDB();
      const invitationsQuery = query(
        collection(db, 'invitations'),
        where('organizationId', '==', user.organizationId)
      );
      const snapshot = await getDocs(invitationsQuery);
      const invitations = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as Invitation));
      setPendingInvitations(invitations.filter(inv => !inv.usedAt));
    } catch (err) {
      console.error('Error loading invitations:', err);
    }
  };

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    if (!firebaseUser && !isLocalImpersonation) return;

    setLoading(true);
    setError('');

    try {
      if (isLocalImpersonation) {
        await settingsApi.updateProfile({
          displayName: displayName.trim(),
        });
        refreshLocalImpersonationData();
      } else {
        const db = getDB();
        await updateDoc(doc(db, 'users', firebaseUser!.uid), {
          displayName: displayName.trim(),
          updatedAt: Timestamp.now(),
        });

        await logUpdate(firebaseUser!.uid, user.organizationId, 'user', firebaseUser!.uid, {
          field: 'displayName',
          oldValue: user.displayName,
          newValue: displayName.trim(),
        });
      }

      showToast({ message: 'Profile updated successfully' });
    } catch (err) {
      showToast({ message: (err as Error).message || 'Failed to update profile', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateOrganization = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isAdmin || !user?.organizationId) {
      setError('Only admins can update organization settings');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const settingsToSave = buildSettingsForSave();

      if (isLocalImpersonation) {
        const updatedOrganization = await settingsApi.updateCurrentOrganization({
          name: organizationName.trim(),
          settings: settingsToSave,
        });
        setOrganizationName(updatedOrganization.name);
        setOrgSettings(mergeOrganizationSettings(updatedOrganization.settings));
        refreshLocalImpersonationData();
      } else {
        const db = getDB();
        await updateDoc(doc(db, 'organizations', user.organizationId), {
          name: organizationName.trim(),
          settings: settingsToSave,
          updatedAt: Timestamp.now(),
        });

        await logOrganizationUpdate(user.id, user.organizationId, {
          name: organizationName.trim(),
          settings: settingsToSave,
        });

        setOrgSettings(settingsToSave);
      }

      showToast({ message: 'Organization settings saved' });
    } catch (err) {
      showToast({ message: (err as Error).message || 'Failed to update organization', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleInviteUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isAdmin || !user?.organizationId) return;
    if (!firebaseUser && !isLocalImpersonation) return;

    if (!inviteEmail.trim()) {
      setError('Please enter an email address');
      return;
    }

    setInviteLoading(true);
    setError('');

    try {
      const normalizedEmail = inviteEmail.trim().toLowerCase();
      const currentSettings = buildSettingsForSave();

      if (!currentSettings.invitesEnabled) {
        setError('Invitations are disabled in settings.');
        return;
      }

      if (currentSettings.allowedDomains.length) {
        const domain = normalizedEmail.split('@').pop() || '';
        if (!domain || !currentSettings.allowedDomains.includes(domain)) {
          setError(`Email domain is not allowed. Allowed domains: ${currentSettings.allowedDomains.join(', ')}`);
          return;
        }
      }

      if (isLocalImpersonation) {
        await settingsApi.createInvitation(buildInvitePayload(normalizedEmail, inviteRole, currentSettings));
        await loadPendingInvitations();
      } else {
        const db = getDB();
        const inviteData = {
          organizationId: user.organizationId,
          email: normalizedEmail,
          role: inviteRole,
          createdBy: firebaseUser!.uid,
          createdAt: Timestamp.now(),
          expiresAt: getInviteExpiryTimestamp(currentSettings),
        };

        await addDoc(collection(db, 'invitations'), inviteData);

        await logUserManagement(firebaseUser!.uid, user.organizationId, 'invite_user', '', {
          email: normalizedEmail,
          role: inviteRole,
        });

        await loadPendingInvitations();
      }

      showToast({ message: `Invitation sent to ${normalizedEmail}` });
      setInviteEmail('');
    } catch (err) {
      showToast({ message: (err as Error).message || 'Failed to send invitation', type: 'error' });
    } finally {
      setInviteLoading(false);
    }
  };

  const handleInviteCustomer = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isAdmin || !user?.organizationId) return;
    if (!firebaseUser && !isLocalImpersonation) return;

    const normalizedEmail = inviteCustomerEmail.trim().toLowerCase();
    if (!normalizedEmail) {
      setError('Please enter a customer email address');
      return;
    }

    setInviteCustomerLoading(true);
    setError('');

    try {
      const currentSettings = buildSettingsForSave();

      if (!currentSettings.clientInvitesEnabled) {
        setError('Customer invitations are disabled in settings.');
        return;
      }

      if (currentSettings.allowedDomains.length) {
        const domain = normalizedEmail.split('@').pop() || '';
        if (!domain || !currentSettings.allowedDomains.includes(domain)) {
          setError(`Email domain is not allowed. Allowed domains: ${currentSettings.allowedDomains.join(', ')}`);
          return;
        }
      }

      if (isLocalImpersonation) {
        await settingsApi.createInvitation(buildInvitePayload(normalizedEmail, 'customer', currentSettings));
        await loadPendingInvitations();
      } else {
        const db = getDB();
        const inviteData = {
          organizationId: user.organizationId,
          email: normalizedEmail,
          role: 'customer' as const,
          createdBy: firebaseUser!.uid,
          createdAt: Timestamp.now(),
          expiresAt: getInviteExpiryTimestamp(currentSettings),
        };

        await addDoc(collection(db, 'invitations'), inviteData);

        await logUserManagement(firebaseUser!.uid, user.organizationId, 'invite_user', '', {
          email: normalizedEmail,
          role: 'customer',
        });

        await loadPendingInvitations();
      }

      showToast({ message: `Customer invitation sent to ${normalizedEmail}` });
      setInviteCustomerEmail('');
    } catch (err) {
      showToast({ message: (err as Error).message || 'Failed to send customer invitation', type: 'error' });
    } finally {
      setInviteCustomerLoading(false);
    }
  };

  const handleRemoveUser = async (targetUserId: string, targetUserName: string) => {
    if (!isAdmin || !user?.organizationId) return;
    if (!firebaseUser && !isLocalImpersonation) return;

    if (targetUserId === (firebaseUser?.uid || user.id)) {
      setError('You cannot remove yourself');
      return;
    }

    if (!window.confirm(`Are you sure you want to remove ${targetUserName} from your organization?`)) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      if (isLocalImpersonation) {
        await settingsApi.removeStaff(targetUserId);
        await loadStaffMembers();
        refreshLocalImpersonationData();
      } else {
        const db = getDB();
        await deleteDoc(doc(db, 'users', targetUserId));

        await logUserManagement(firebaseUser!.uid, user.organizationId, 'remove_user', targetUserId, {
          removedUserName: targetUserName,
        });

        await loadStaffMembers();
      }

      showToast({ message: `${targetUserName} has been removed` });
    } catch (err) {
      showToast({ message: (err as Error).message || 'Failed to remove user', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleCancelInvitation = async (invitationId: string, email: string) => {
    if (!isAdmin || !user?.organizationId) return;
    if (!firebaseUser && !isLocalImpersonation) return;

    if (!window.confirm(`Are you sure you want to cancel the invitation to ${email}?`)) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      if (isLocalImpersonation) {
        await settingsApi.cancelInvitation(invitationId);
      } else {
        const db = getDB();
        await deleteDoc(doc(db, 'invitations', invitationId));

        await logUserManagement(firebaseUser!.uid, user.organizationId, 'remove_user', '', {
          action: 'cancel_invitation',
          email,
        });
      }

      showToast({ message: `Invitation to ${email} cancelled` });
      await loadPendingInvitations();
    } catch (err) {
      showToast({ message: (err as Error).message || 'Failed to cancel invitation', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleResendInvitation = async (invitation: Invitation) => {
    if (!isAdmin || !user?.organizationId) return;
    if (!firebaseUser && !isLocalImpersonation) return;

    setInviteLoading(true);
    setError('');

    try {
      const currentSettings = buildSettingsForSave();
      const normalizedEmail = invitation.email?.toLowerCase() || '';

      if (!normalizedEmail) {
        setError('Invitation email is missing.');
        return;
      }

      if (!currentSettings.invitesEnabled) {
        setError('Invitations are disabled in settings.');
        return;
      }

      if (currentSettings.allowedDomains.length) {
        const domain = normalizedEmail.split('@').pop() || '';
        if (!domain || !currentSettings.allowedDomains.includes(domain)) {
          setError(`Email domain is not allowed. Allowed domains: ${currentSettings.allowedDomains.join(', ')}`);
          return;
        }
      }

      if (isLocalImpersonation) {
        await settingsApi.resendInvitation(invitation.id, toIsoTimestamp(getInviteExpiryTimestamp(currentSettings)));
      } else {
        const db = getDB();
        const newInviteData = {
          organizationId: user.organizationId,
          email: normalizedEmail,
          role: invitation.role,
          createdBy: firebaseUser!.uid,
          createdAt: Timestamp.now(),
          expiresAt: getInviteExpiryTimestamp(currentSettings),
        };

        await addDoc(collection(db, 'invitations'), newInviteData);
        await deleteDoc(doc(db, 'invitations', invitation.id));

        await logUserManagement(firebaseUser!.uid, user.organizationId, 'invite_user', '', {
          email: invitation.email,
          role: invitation.role,
          action: 'resend',
        });
      }

      showToast({ message: `Invitation resent to ${invitation.email}` });
      await loadPendingInvitations();
    } catch (err) {
      showToast({ message: (err as Error).message || 'Failed to resend invitation', type: 'error' });
    } finally {
      setInviteLoading(false);
    }
  };

  const handleUpdateUserRole = async (targetUserId: string, newRole: 'admin' | 'member', targetUserName: string) => {
    if (!isAdmin || !user?.organizationId) return;
    if (!firebaseUser && !isLocalImpersonation) return;

    if (targetUserId === (firebaseUser?.uid || user.id)) {
      setError('You cannot change your own role');
      return;
    }

    setLoading(true);
    setError('');

    try {
      if (isLocalImpersonation) {
        await settingsApi.updateStaffRole(targetUserId, newRole);
        await loadStaffMembers();
        refreshLocalImpersonationData();
      } else {
        const db = getDB();
        await updateDoc(doc(db, 'users', targetUserId), {
          role: newRole,
          updatedAt: Timestamp.now(),
        });

        await logUserManagement(firebaseUser!.uid, user.organizationId, 'change_role', targetUserId, {
          oldRole: staffMembers.find(u => u.id === targetUserId)?.role,
          newRole,
        });

        await loadStaffMembers();
      }

      showToast({ message: `${targetUserName}'s role updated to ${newRole}` });
    } catch (err) {
      showToast({ message: (err as Error).message || 'Failed to update user role', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return <div>Loading...</div>;
  }

  return (
    <div className="settings-container">
      <div className="settings-header">
        <h1>Settings</h1>
      </div>

      <div className="settings-tabs">
        <button
          className={`settings-tab ${activeTab === 'profile' ? 'active' : ''}`}
          onClick={() => setActiveTab('profile')}
        >
          <HybridIcon name="person" size={20} color={activeTab === 'profile' ? '#f4511e' : '#666'} />
          Profile
        </button>
        {isAdmin && (
          <button
            className={`settings-tab ${activeTab === 'organization' ? 'active' : ''}`}
            onClick={() => setActiveTab('organization')}
          >
            <HybridIcon name="business" size={20} color={activeTab === 'organization' ? '#f4511e' : '#666'} />
            Organization
          </button>
        )}
        {isAdmin && (
          <button
            className={`settings-tab ${activeTab === 'staff' ? 'active' : ''}`}
            onClick={() => setActiveTab('staff')}
          >
            <HybridIcon name="people" size={20} color={activeTab === 'staff' ? '#f4511e' : '#666'} />
            Staff Management
          </button>
        )}
        <button
          className={`settings-tab ${activeTab === 'app' ? 'active' : ''}`}
          onClick={() => setActiveTab('app')}
        >
          <HybridIcon name="settings" size={20} color={activeTab === 'app' ? '#f4511e' : '#666'} />
          App
        </button>
      </div>

      <div className="settings-content">
        {error && <div className="settings-message settings-error">{error}</div>}
        {isLocalImpersonation && (
          <div className="settings-message settings-success">
            Local impersonation is active. Settings actions on this page run through the local backend using your selected org, user, and role.
          </div>
        )}

        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <div className="settings-section">
            <h2>Profile Settings</h2>
            <form onSubmit={handleUpdateProfile}>
              <div className="settings-field">
                <label>Display Name</label>
                <input
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  required
                  disabled={loading}
                />
              </div>
              <div className="settings-field">
                <label>Email</label>
                <input
                  type="email"
                  value={email}
                  disabled
                  title="Email cannot be changed. Contact support if you need to update your email."
                />
                <small>Email cannot be changed</small>
              </div>
              <div className="settings-field">
                <label>Role</label>
                <input
                  type="text"
                  value={user.role}
                  disabled
                />
              </div>
              <button type="submit" className="settings-button" disabled={loading}>
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </form>
          </div>
        )}

        {/* Organization Tab */}
        {activeTab === 'organization' && isAdmin && (
          <div className="settings-section">
            <h2>Organization Settings</h2>
            <form onSubmit={handleUpdateOrganization}>
              <div className="settings-field">
                <label>Organization Name</label>
                <input
                  type="text"
                  value={organizationName}
                  onChange={(e) => setOrganizationName(e.target.value)}
                  required
                  disabled={loading}
                />
              </div>
              {organization && (
                <div className="settings-field">
                  <label>Organization ID</label>
                  <input
                    type="text"
                    value={organization.id}
                    disabled
                  />
                  <small>This ID cannot be changed</small>
                </div>
              )}

              <div className="org-cards">
                <div className="org-card">
                  <div className="org-card-header">
                    <div>
                      <h3>Billing & Pricing</h3>
                      <p className="org-card-caption">Choose whether bookings collect fees or are schedule-only.</p>
                    </div>
                  </div>
                  <div className="settings-radio-group">
                    <label className="radio-option">
                      <input
                        type="radio"
                        name="billingMode"
                        value="paid"
                        checked={orgSettings.billingMode === 'paid'}
                        onChange={(e) =>
                          setOrgSettings((prev) => ({ ...prev, billingMode: e.target.value as OrganizationSettings['billingMode'] }))
                        }
                        disabled={loading}
                      />
                      <div>
                        <div className="option-title">Standard billing</div>
                        <div className="option-caption">Bookings can collect fees.</div>
                      </div>
                    </label>
                    <label className="radio-option">
                      <input
                        type="radio"
                        name="billingMode"
                        value="fleet_only"
                        checked={orgSettings.billingMode === 'fleet_only'}
                        onChange={(e) =>
                          setOrgSettings((prev) => ({ ...prev, billingMode: e.target.value as OrganizationSettings['billingMode'] }))
                        }
                        disabled={loading}
                      />
                      <div>
                        <div className="option-title">No-fee (fleet only)</div>
                        <div className="option-caption">Use bookings to manage schedule without payments.</div>
                      </div>
                    </label>
                  </div>
                </div>

                <div className="org-card">
                  <div className="org-card-header">
                    <div>
                      <h3>Access & Invites</h3>
                      <p className="org-card-caption">Control who can join and how they’re invited.</p>
                    </div>
                  </div>
                  <div className="toggle-row">
                    <div className="toggle-switch">
                      <input
                        type="checkbox"
                        id="invitesEnabled"
                        checked={orgSettings.invitesEnabled}
                        onChange={(e) =>
                          setOrgSettings((prev) => ({ ...prev, invitesEnabled: e.target.checked }))
                        }
                        disabled={loading}
                      />
                      <span className="toggle-slider" />
                    </div>
                    <div className="toggle-text">
                      <label htmlFor="invitesEnabled">Allow staff invitations</label>
                      <small>Admins can invite teammates when enabled.</small>
                    </div>
                  </div>
                  <div className="toggle-row">
                    <div className="toggle-switch">
                      <input
                        type="checkbox"
                        id="clientInvitesEnabled"
                        checked={orgSettings.clientInvitesEnabled}
                        onChange={(e) =>
                          setOrgSettings((prev) => ({ ...prev, clientInvitesEnabled: e.target.checked }))
                        }
                        disabled={loading}
                      />
                      <span className="toggle-slider" />
                    </div>
                    <div className="toggle-text">
                      <label htmlFor="clientInvitesEnabled">Allow customer invitations</label>
                      <small>Customer managers can invite customers to book.</small>
                    </div>
                  </div>
                  <div className="settings-inline-fields">
                    <div className="settings-field">
                      <label>Invite expiry (days)</label>
                      <input
                        type="number"
                        min={1}
                        max={90}
                        value={orgSettings.inviteExpiryDays}
                        onChange={(e) =>
                          setOrgSettings((prev) => ({ ...prev, inviteExpiryDays: Number(e.target.value) }))
                        }
                        disabled={loading}
                      />
                    </div>
                  </div>
                  <div className="settings-field">
                    <label>Allowed domains</label>
                    <div className="domain-input-row">
                      <input
                        type="text"
                        value={domainInput}
                        onChange={(e) => setDomainInput(e.target.value)}
                        placeholder="company.com"
                        disabled={loading}
                      />
                      <button type="button" className="secondary-button" onClick={handleAddDomain} disabled={loading}>
                        Add
                      </button>
                    </div>
                    <small>Restrict sign-in and invites to these domains. Leave empty for no restriction.</small>
                    {orgSettings.allowedDomains.length > 0 ? (
                      <div className="domain-chip-list">
                        {orgSettings.allowedDomains.map((domain) => (
                          <div key={domain} className="domain-chip">
                            <span>{domain}</span>
                            <button type="button" onClick={() => handleRemoveDomain(domain)} aria-label={`Remove ${domain}`}>
                              <HybridIcon name="close" size={14} color="#666" />
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="domain-empty">No domain restrictions</div>
                    )}
                  </div>
                </div>

                <div className="org-card">
                  <div className="org-card-header">
                    <div>
                      <h3>Booking Rules</h3>
                      <p className="org-card-caption">Control visibility, approvals, and booking windows.</p>
                    </div>
                  </div>
                  <div className="toggle-row">
                    <div className="toggle-switch">
                      <input
                        type="checkbox"
                        id="clientBookingEnabled"
                        checked={orgSettings.booking.clientBookingEnabled}
                        onChange={(e) =>
                          setOrgSettings((prev) => ({
                            ...prev,
                            booking: { ...prev.booking, clientBookingEnabled: e.target.checked },
                          }))
                        }
                        disabled={loading}
                      />
                      <span className="toggle-slider" />
                    </div>
                    <div className="toggle-text">
                      <label htmlFor="clientBookingEnabled">Allow invited customers to book vehicles</label>
                    </div>
                  </div>
                  <div className="toggle-row">
                    <div className="toggle-switch">
                      <input
                        type="checkbox"
                        id="showAllVehiclesToClients"
                        checked={orgSettings.booking.showAllVehiclesToClients}
                        onChange={(e) =>
                          setOrgSettings((prev) => ({
                            ...prev,
                            booking: { ...prev.booking, showAllVehiclesToClients: e.target.checked },
                          }))
                        }
                        disabled={loading}
                      />
                      <span className="toggle-slider" />
                    </div>
                    <div className="toggle-text">
                      <label htmlFor="showAllVehiclesToClients">Show all vehicles to customers</label>
                      <small>When off, only vehicles marked as bookable should appear to customers.</small>
                    </div>
                  </div>
                  <div className="toggle-row">
                    <div className="toggle-switch">
                      <input
                        type="checkbox"
                        id="requiresApproval"
                        checked={orgSettings.booking.requiresApproval}
                        onChange={(e) =>
                          setOrgSettings((prev) => ({
                            ...prev,
                            booking: { ...prev.booking, requiresApproval: e.target.checked },
                          }))
                        }
                        disabled={loading}
                      />
                      <span className="toggle-slider" />
                    </div>
                    <div className="toggle-text">
                      <label htmlFor="requiresApproval">Require manager approval for bookings</label>
                    </div>
                  </div>
                  <div className="settings-inline-fields">
                    <div className="settings-field">
                      <label>Cancellation/reschedule cutoff (hours)</label>
                      <input
                        type="number"
                        min={0}
                        value={orgSettings.booking.cancellationCutoffHours}
                        onChange={(e) =>
                          setOrgSettings((prev) => ({
                            ...prev,
                            booking: { ...prev.booking, cancellationCutoffHours: Number(e.target.value) },
                          }))
                        }
                        disabled={loading}
                      />
                    </div>
                    <div className="settings-field">
                      <label>Booking window start (days from today)</label>
                      <input
                        type="number"
                        min={0}
                        value={orgSettings.booking.bookingWindowStartDays}
                        onChange={(e) =>
                          setOrgSettings((prev) => ({
                            ...prev,
                            booking: { ...prev.booking, bookingWindowStartDays: Number(e.target.value) },
                          }))
                        }
                        disabled={loading}
                      />
                    </div>
                    <div className="settings-field">
                      <label>Booking window end (days from today)</label>
                      <input
                        type="number"
                        min={0}
                        value={orgSettings.booking.bookingWindowEndDays}
                        onChange={(e) =>
                          setOrgSettings((prev) => ({
                            ...prev,
                            booking: { ...prev.booking, bookingWindowEndDays: Number(e.target.value) },
                          }))
                        }
                        disabled={loading}
                      />
                    </div>
                  </div>
                </div>

                <div className="org-card">
                  <div className="org-card-header">
                    <div>
                      <h3>Notifications</h3>
                      <p className="org-card-caption">Control who gets notified about bookings.</p>
                    </div>
                  </div>
                  <div className="toggle-row">
                    <div className="toggle-switch">
                      <input
                        type="checkbox"
                        id="bookingEmails"
                        checked={orgSettings.notifications.bookingEmails}
                        onChange={(e) =>
                          setOrgSettings((prev) => ({
                            ...prev,
                            notifications: { ...prev.notifications, bookingEmails: e.target.checked },
                          }))
                        }
                        disabled={loading}
                      />
                      <span className="toggle-slider" />
                    </div>
                    <div className="toggle-text">
                      <label htmlFor="bookingEmails">Email managers on booking changes</label>
                    </div>
                  </div>
                  <div className="settings-field">
                    <label>Manager digest cadence</label>
                    <select
                      value={orgSettings.notifications.managerDigest}
                      onChange={(e) =>
                        setOrgSettings((prev) => ({
                          ...prev,
                          notifications: {
                            ...prev.notifications,
                            managerDigest: e.target.value as OrganizationSettings['notifications']['managerDigest'],
                          },
                        }))
                      }
                      disabled={loading}
                    >
                      <option value="instant">Instant</option>
                      <option value="daily">Daily summary</option>
                    </select>
                  </div>
                </div>

                <button type="submit" className="settings-button save-button" disabled={loading}>
                  {loading ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Staff Management Tab */}
        {activeTab === 'staff' && isAdmin && (
          <div className="settings-section">
            <h2>Staff Management</h2>
            
            {/* Invite User Form */}
            <div className="staff-invite-section">
                <h3>Invite New User</h3>
              <form onSubmit={handleInviteUser} className="staff-invite-form">
                <div className="settings-field">
                  <label>Email Address</label>
                  <input
                    type="email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="user@example.com"
                    required
                    disabled={inviteLoading}
                  />
                </div>
                <div className="settings-field">
                  <label>Role</label>
                  <select
                    value={inviteRole}
                    onChange={(e) => setInviteRole(e.target.value as 'admin' | 'member')}
                    disabled={inviteLoading}
                  >
                    <option value="member">Member</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                <div className="settings-field button-cell">
                  <button type="submit" className="settings-button" disabled={inviteLoading}>
                    {inviteLoading ? 'Sending...' : 'Send Invitation'}
                  </button>
                </div>
              </form>
            </div>

            {/* Customer Invite Form */}
            <div className="staff-invite-section">
              <h3>Invite Customer</h3>
              <form onSubmit={handleInviteCustomer} className="staff-invite-form">
                <div className="settings-field">
                  <label>Customer Email</label>
                  <input
                    type="email"
                    value={inviteCustomerEmail}
                    onChange={(e) => setInviteCustomerEmail(e.target.value)}
                    placeholder="customer@example.com"
                    required
                    disabled={inviteCustomerLoading}
                  />
                  {!orgSettings.clientInvitesEnabled && (
                    <small>Customer invitations are disabled in settings.</small>
                  )}
                </div>
                <div className="settings-field role-field">
                  <label>Role</label>
                  <input type="text" value="Customer" disabled />
                </div>
                <div className="settings-field button-cell">
                  <button type="submit" className="settings-button" disabled={inviteCustomerLoading || !orgSettings.clientInvitesEnabled}>
                    {inviteCustomerLoading ? 'Sending...' : 'Send Customer Invite'}
                  </button>
                </div>
              </form>
            </div>

            {/* Pending Invitations */}
            {pendingInvitations.length > 0 && (
              <div className="staff-invitations-section">
                <h3>Pending Invitations ({pendingInvitations.length})</h3>
                <div className="invitations-list">
                  {pendingInvitations.map((invitation) => (
                    <div key={invitation.id} className="invitation-card">
                      <div className="invitation-info">
                        <div className="invitation-email">{invitation.email}</div>
                        <div className="invitation-details">
                          <span className="invitation-role">{invitation.role}</span>
                          {formatDateValue(invitation.expiresAt) && (
                            <span className="invitation-expiry">
                              Expires: {formatDateValue(invitation.expiresAt)}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="invitation-actions">
                        <button
                          onClick={() => handleResendInvitation(invitation)}
                          disabled={inviteLoading || loading}
                          className="invitation-resend-button"
                        >
                          <HybridIcon name="refresh" size={16} color="#1976d2" />
                          Resend
                        </button>
                        <button
                          onClick={() => handleCancelInvitation(invitation.id, invitation.email || '')}
                          disabled={loading}
                          className="invitation-cancel-button"
                        >
                          <HybridIcon name="close-circle" size={16} color="#d32f2f" />
                          Cancel
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Staff List */}
            <div className="staff-list-section">
              <div className="staff-list-header">
                <h3>Current Staff ({staffMembers.length})</h3>
                <input
                  type="text"
                  placeholder="Search staff..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="staff-search-input"
                />
              </div>
              <div className="staff-list">
                {staffMembers
                  .filter((member) => {
                    if (!searchQuery.trim()) return true;
                    const query = searchQuery.toLowerCase();
                    return (
                      member.displayName?.toLowerCase().includes(query) ||
                      member.email?.toLowerCase().includes(query)
                    );
                  })
                  .map((member) => (
                    <div key={member.id} className="staff-member-card">
                      <div className="staff-member-info">
                        <div className="staff-member-name">
                          {member.displayName}
                          {member.id === (firebaseUser?.uid || user.id) && <span className="staff-badge">You</span>}
                          {member.role === 'admin' && <span className="staff-badge admin">Admin</span>}
                        </div>
                        <div className="staff-member-email">{member.email}</div>
                        {formatDateValue(member.createdAt) && (
                          <div className="staff-member-joined">
                            Joined: {formatDateValue(member.createdAt)}
                          </div>
                        )}
                      </div>
                      {member.id !== (firebaseUser?.uid || user.id) && (
                        <div className="staff-member-actions">
                          <select
                            value={member.role}
                            onChange={(e) => handleUpdateUserRole(member.id, e.target.value as 'admin' | 'member', member.displayName)}
                            disabled={loading}
                            className="staff-role-select"
                          >
                            <option value="member">Member</option>
                            <option value="admin">Admin</option>
                          </select>
                          <button
                            onClick={() => handleRemoveUser(member.id, member.displayName)}
                            disabled={loading}
                            className="staff-remove-button"
                          >
                            <HybridIcon name="trash" size={18} color="#d32f2f" />
                            Remove
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                {staffMembers.filter((member) => {
                  if (!searchQuery.trim()) return false;
                  const query = searchQuery.toLowerCase();
                  return (
                    member.displayName?.toLowerCase().includes(query) ||
                    member.email?.toLowerCase().includes(query)
                  );
                }).length === 0 && searchQuery.trim() && (
                  <div className="staff-empty-state">
                    <p>No staff members found matching "{searchQuery}"</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* App Tab */}
        {activeTab === 'app' && (
          <div className="settings-section">
            <h2>App Preferences</h2>

            <div className="org-cards">
              <div className="org-card">
                <div className="org-card-header">
                  <div>
                    <h3>Theme</h3>
                    <p className="org-card-caption">Choose your preferred appearance for Path Finder.</p>
                  </div>
                </div>

                <div className="settings-radio-group theme-radio-group">
                  <label className={`radio-option theme-radio-option ${theme === 'light' ? 'is-selected' : ''}`}>
                    <input
                      type="radio"
                      name="theme"
                      value="light"
                      checked={theme === 'light'}
                      onChange={() => setTheme('light')}
                    />
                    <span className="theme-radio-indicator" aria-hidden="true" />
                    <div>
                      <div className="option-title">Light</div>
                      <div className="option-caption">Always use the light theme.</div>
                    </div>
                  </label>

                  <label className={`radio-option theme-radio-option ${theme === 'dark' ? 'is-selected' : ''}`}>
                    <input
                      type="radio"
                      name="theme"
                      value="dark"
                      checked={theme === 'dark'}
                      onChange={() => setTheme('dark')}
                    />
                    <span className="theme-radio-indicator" aria-hidden="true" />
                    <div>
                      <div className="option-title">Dark</div>
                      <div className="option-caption">Always use the dark theme.</div>
                    </div>
                  </label>

                  <label className={`radio-option theme-radio-option ${theme === 'system' ? 'is-selected' : ''}`}>
                    <input
                      type="radio"
                      name="theme"
                      value="system"
                      checked={theme === 'system'}
                      onChange={() => setTheme('system')}
                    />
                    <span className="theme-radio-indicator" aria-hidden="true" />
                    <div>
                      <div className="option-title">Use device setting</div>
                      <div className="option-caption">Current applied theme: {effectiveTheme}.</div>
                    </div>
                  </label>
                </div>
              </div>

              <div className="org-card">
                <div className="org-card-header">
                  <div>
                    <h3>Install App</h3>
                    <p className="org-card-caption">Install Path Finder for a faster launch experience.</p>
                  </div>
                </div>
                <div className="app-install-section">
                  {isInstalled ? (
                    <div className="app-install-status">
                      <HybridIcon name="checkmark-circle" size={48} color="#4caf50" />
                      <h3>App Installed</h3>
                      <p>Path Finder is installed on your device. You can access it from your home screen or app drawer.</p>
                    </div>
                  ) : isInstallable ? (
                    <div className="app-install-prompt">
                      <HybridIcon name="settings" size={48} color="#f4511e" />
                      <h3>Install Path Finder</h3>
                      <p>Install Path Finder as a Progressive Web App (PWA) to access it quickly from your home screen, even offline.</p>
                      <button
                        onClick={async () => {
                          const installed = await install();
                          if (installed) {
                            showToast({ message: 'App installed successfully.', type: 'success' });
                          }
                        }}
                        className="app-install-button"
                      >
                        <HybridIcon name="add-circle" size={20} color="#fff" />
                        Install App
                      </button>
                    </div>
                  ) : (
                    <div className="app-install-info">
                      <HybridIcon name="help-circle" size={48} color="#666" />
                      <h3>Installation Not Available</h3>
                      <p>Your browser does not support app installation, or the app is already installed.</p>
                      <div className="app-install-instructions">
                        <h4>How to install on different devices:</h4>
                        <ul>
                          <li><strong>Chrome/Edge (Desktop):</strong> Look for the install icon in the address bar</li>
                          <li><strong>Chrome (Android):</strong> Tap the menu and select "Install app"</li>
                          <li><strong>Safari (iOS):</strong> Tap Share and select "Add to Home Screen"</li>
                        </ul>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
